import Aside from '@components/Aside/Aside';
import CreativeModalContent from '@components/CreativeModalContent/CreativeModalContent';
import type { TCreativeModalType } from '@components/CreativeModalContent/types';
import Loader from '@components/Loader/Loader';
import QuoteRequestModal from '@components/QuoteRequestModal';
import NearestStores from '@components/StoreCard/NearestStores';
import { CONFIGURATOR_CREATIVE_BASE_URL, CONFIGURATOR_URI, IS_SHOP, SUPPORTED_OFFERS } from '@config/index';
import { AVAILABLE_REGIONS, DISPLAY_ECO_TAX_INFO } from '@const/index';
import alertsStore from '@context/global/mobx/alertStore';
import NextCheckoutApi from '@features/api/nextApiService/nextCheckoutApi';
import productCreativeAdapter from '@features/cart/adapters/configuratorCreative';
import productFrontAdapter from '@features/cart/adapters/configuratorFront';
import { useCartStore } from '@features/cart/mobx/context/CartStoreProvider';
import type { ICartData, IConfiguratorCreativeProduct, ICreativeData, ICreativeExtraData, ICreativeImages, IFrontConfiguratorData, IFrontConfiguratorImages, IFrontConfiguratorProduct, IFrontConfiguratorQuoteRequest } from '@features/cart/typescript';
import type { IPointOfPresence } from '@features/checkout/typescript';
import type { ICmsContent } from '@features/cms/typescript';
import layoutUiState from '@features/layout/mobX/LayoutUiState';
import ConfiguratorFrame from '@features/productPreview/sections/FrontConfigurator/ConfiguratorFrame';
import styles from '@features/productPreview/sections/FrontConfigurator/FrontConfigurator.styles';
import ProductCardDocument from '@features/productPreview/sections/FrontConfigurator/ProductCardDocument';
import type { IProductConfigResponse } from '@features/productPreview/typescript';
import { useOffer } from '@hooks/useOffer';
import useRequestProgress from '@hooks/useRequestProgress';
import { convertToSlug, downloadFile } from '@utils/downloadFile';
import getCrosslinkProductUrl from '@utils/getCrosslinkProductUrl';
import getPublicEnv from '@utils/getPublicEnv';
import logger from '@utils/logger';
import { getLanguage, getRegion } from '@utils/misc';
import { sanitizeCart, sanitizeObjectUuid } from '@utils/sanitizeProductQueryParams';
import scrollToElementById from '@utils/scrollToElementById';
import type { IInitOptions, IProductConfiguration, IShowroom } from '@vox/front-configurator';
import { t } from 'i18next';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import { type ForwardedRef, forwardRef, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
type PropsType = {
  configuratorData: IProductConfiguration;
  configuratorConfig: IInitOptions;
  productCardLinksDocuments?: Record<number, ICmsContent[]>;
  config: IProductConfigResponse | null;
  productCardTitles: string[];
};
const FrontConfigurator = forwardRef(({
  configuratorData,
  configuratorConfig,
  productCardLinksDocuments,
  config,
  productCardTitles
}: PropsType, ref: ForwardedRef<HTMLDivElement>) => {
  const region = getRegion();
  const {
    data: session,
    status: sessionStatus
  } = useSession();
  const cartStore = useCartStore();
  const {
    setCartCreated
  } = layoutUiState;
  const [cart, setCart] = useState<string | null>(null);
  const [isNearestStoresOpened, setIsNearestStoresOpened] = useState<boolean>(false);
  const [firstShowroom, setFirstShowroom] = useState<IPointOfPresence | null>(null);
  const [isQuoteRequestModalOpen, setIsQuoteRequestModalOpen] = useState<boolean>(false);
  const [quoteRequestData, setQuoteRequestData] = useState<IFrontConfiguratorQuoteRequest | null>(null);
  const [isProductCardDocumentsOpened, setIsProductCardDocumentsOpened] = useState<number | null>(null);
  const [offer] = useOffer();
  const {
    offer: regionOffer,
    pricelists: regionPriceList,
    storages: regionStorage
  } = configuratorConfig.region;
  const router = useRouter();
  const {
    locale,
    query,
    asPath
  } = router;
  /** Remount MainFrame po nawigacji, gdy @vox/front-configurator zmemoizował config przy starym window (listing → karta z ?objectUuid=). */
  const [creativeRemountNonce, setCreativeRemountNonce] = useState(0);
  const configuratorConfigRef = useRef(configuratorConfig);
  configuratorConfigRef.current = configuratorConfig;
  const firstShowroomResolversRef = useRef<((showroom: IShowroom) => void)[]>([]);
  const lang = getLanguage(locale);
  const [valueUuId, setValueUuId] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [valueModal, setValueModal] = useState(false);
  const [urlParams, setUrlParams] = useState(new URLSearchParams());
  const [creativeModalType, setCreativeModalType] = useState<TCreativeModalType>('saveProject');
  const [projectCreated, setProjectCreated] = useState(false);
  const [isConfiguratorReady, setIsConfiguratorReady] = useState(false);
  const [isPageTransitioning, setIsPageTransitioning] = useState(false);
  const handleScrollUp = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);

  /**
   * objectUuid z adresu: router bywa opóźniony przy klienckiej nawigacji (pierwszy render bez query),
   * a window.location.search od razu odpowiada paskowi adresu — tak jak przy pełnym odświeżeniu.
   */
  const objectUuidFromClientLocation = typeof window !== 'undefined' ? sanitizeObjectUuid(new URLSearchParams(window.location.search).get('objectUuid')) : null;
  const objectUuidFromRouter = useMemo(() => {
    const raw = query.objectUuid;
    const fromQuery = typeof raw === 'string' ? sanitizeObjectUuid(raw) : Array.isArray(raw) && raw[0] ? sanitizeObjectUuid(raw[0]) : null;
    if (fromQuery) return fromQuery;
    const queryStart = asPath.indexOf('?');
    const search = queryStart >= 0 ? asPath.slice(queryStart) : '';
    return sanitizeObjectUuid(new URLSearchParams(search).get('objectUuid'));
  }, [asPath, query.objectUuid]);
  const objectUuidFromUrl = objectUuidFromClientLocation || objectUuidFromRouter;
  const objectUuid = objectUuidFromUrl || sanitizeObjectUuid(configuratorConfig.objectUuid || null) || '';
  useEffect(() => {
    const syncFrameAfterUrlSettled = () => {
      if (typeof window === 'undefined') return;
      const winUuid = sanitizeObjectUuid(new URLSearchParams(window.location.search).get('objectUuid')) || '';
      if (!winUuid) return;
      const propUuid = sanitizeObjectUuid(configuratorConfigRef.current.objectUuid || null) || '';
      if (winUuid !== propUuid) {
        setIsPageTransitioning(true);
        setCreativeRemountNonce(n => n + 1);
      }
    };
    router.events.on('routeChangeComplete', syncFrameAfterUrlSettled);
    syncFrameAfterUrlSettled();
    return () => {
      router.events.off('routeChangeComplete', syncFrameAfterUrlSettled);
    };
  }, [router.events]);
  useEffect(() => {
    const queryStart = asPath.indexOf('?');
    const search = queryStart >= 0 ? asPath.slice(queryStart) : '';
    const urlParamsCurrent = new URLSearchParams(search);
    setUrlParams(urlParamsCurrent);
    const safe = sanitizeCart(urlParamsCurrent.get('cart'));
    if (safe) setCart(safe);
  }, [asPath]);
  const handleSaveCreative = useCallback(() => {
    const creativeAddSaveProjectButton = document.querySelectorAll('.price-container');
    const safeObjectUuid = urlParams ? sanitizeObjectUuid(urlParams.get('objectUuid')) : null;
    if (creativeAddSaveProjectButton && urlParams?.get('saveCreative') && safeObjectUuid) {
      setCreativeModalType('saveProject');
      setValueUuId(safeObjectUuid);
      setImageUrl(urlParams.get('imageUrl') ?? '');
      setValueModal(true);
    }
  }, [urlParams]);
  const handleOverflowHtml = useCallback(() => {
    const htmlObj = document.querySelector('html');
    if (htmlObj) {
      htmlObj.style.overflowY = 'unset';
    }
  }, []);
  const [addProducts] = useRequestProgress(async (productArray: ICartData[], isQuantityChanged = false, objectUuid?: string) => {
    if (productArray.length > 0) {
      const id = await cartStore.createCart();
      setCartCreated(true);
      const addPositionsResponse = await NextCheckoutApi.addPositions(id, productArray);
      if (!addPositionsResponse.success) {
        alertsStore.pushAlert({
          message: t('alerts.header'),
          display: true
        });
        return;
      }
      await cartStore.fetchAndPopulateCartStore();
      if (isQuantityChanged) {
        cartStore.setAlertReducedQuantity(objectUuid ?? '', configuratorData.id.toString());
      }
    } else if (isQuantityChanged) {
      await cartStore.fetchAndPopulateCartStore(false);
      cartStore.setAlertReducedQuantity(objectUuid ?? '', configuratorData.id.toString());
    }
    cartStore.openMiniSideBarCart();
  });
  const getShowroomUrl = useCallback(() => {
    switch (lang) {
      case 'pl':
        return `${getPublicEnv('NEXT_PUBLIC_HOST')}/pages/uslugi-projektowania`;
      case 'en':
        return `${getPublicEnv('NEXT_PUBLIC_HOST')}/pages/voxbox`;
      case 'es':
        return `${getPublicEnv('NEXT_PUBLIC_HOST')}/voxbox`;
      default:
        return '';
    }
  }, [lang]);
  const isDip = () => configuratorData.behaviour === 'dip_index' || configuratorData.behaviour === 'dip_pw';
  const productFrameKey = `${configuratorData.id}-${objectUuid}-${String(configuratorConfig.configuratorType ?? 'main')}-${creativeRemountNonce}`;
  useEffect(() => {
    const startTransition = () => {
      setIsPageTransitioning(true);
    };
    const finishTransition = () => {
      const startedAt = Date.now();
      const waitForShell = () => {
        const hasShell = Boolean(document.querySelector('#frontConfigurator, #creativeConfigurator'));
        if (hasShell || Date.now() - startedAt > 4000) {
          setIsPageTransitioning(false);
          return;
        }
        requestAnimationFrame(waitForShell);
      };
      requestAnimationFrame(waitForShell);
    };
    router.events.on('routeChangeStart', startTransition);
    router.events.on('routeChangeComplete', finishTransition);
    router.events.on('routeChangeError', finishTransition);
    return () => {
      router.events.off('routeChangeStart', startTransition);
      router.events.off('routeChangeComplete', finishTransition);
      router.events.off('routeChangeError', finishTransition);
    };
  }, [router.events]);
  const handleProductNavigate = useCallback((url: string) => router.push(url), [router]);
  const handleConfiguratorReady = useCallback(() => {
    setIsConfiguratorReady(true);
    setIsPageTransitioning(false);
  }, []);
  useEffect(() => {
    if (!firstShowroom) return;
    const showroom = firstShowroom as IShowroom;
    firstShowroomResolversRef.current.forEach(resolve => resolve(showroom));
    firstShowroomResolversRef.current = [];
  }, [firstShowroom]);
  useEffect(() => {
    return () => {
      firstShowroomResolversRef.current = [];
    };
  }, []);
  const getFirstShowroom = useCallback((): Promise<IShowroom> => {
    if (firstShowroom) {
      return Promise.resolve(firstShowroom as IShowroom);
    }
    return new Promise<IShowroom>(resolve => {
      firstShowroomResolversRef.current.push(resolve);
    });
  }, [firstShowroom]);
  const FrontConfiguratorConfig = useMemo(() => ({
    ...configuratorConfig,
    objectUuid,
    cart: cart ?? configuratorConfig.cart ?? '',
    /** Ten sam host co CONFIGURATOR_URI — MainFrame buduje /api/literal-object/ z productUrl; samo NEXT_PUBLIC_* może być puste. */
    productUrl: CONFIGURATOR_URI,
    async onAddToCart(data: IFrontConfiguratorData, images: IFrontConfiguratorImages, count: number) {
      const product: IFrontConfiguratorProduct = {
        id: typeof configuratorData.id === 'number' ? configuratorData.id : parseInt(configuratorData.id, 10),
        data,
        images,
        count
      };
      const configuredProductId = data.cartData?.products?.[0]?.productId?.toString() ?? data.objectUuid ?? product.id.toString();
      const productCartQuantity = cartStore.getProductQuantityById(configuredProductId);
      const {
        products: cartProducts,
        maxQuantityReached
      } = productFrontAdapter(product, images, productCartQuantity);
      if (maxQuantityReached) {
        cartStore.notifyMaxQuantityReached(configuredProductId);
        return;
      }
      if (cartProducts.length > 0) {
        cartStore.maxQuantityBlockedWithoutProduct = false;
        await addProducts(cartProducts, false, data.objectUuid.toString());
      }
    },
    ...(offer === SUPPORTED_OFFERS.pl && {
      async onQuoteRequest(quoteRequestConfiguration: IFrontConfiguratorQuoteRequest | null) {
        setQuoteRequestData(quoteRequestConfiguration);
        setIsQuoteRequestModalOpen(true);
      }
    }),
    ...(offer === SUPPORTED_OFFERS.pl && {
      async onSeeInShowroom() {
        setIsNearestStoresOpened(true);
      }
    }),
    ...(offer === SUPPORTED_OFFERS.global && {
      async onSeeInShowroom() {
        window.open('/global/showrooms', '_top');
      }
    }),
    ...(offer === SUPPORTED_OFFERS.global && {
      async onSeeInVoxbox() {
        window.open('/global/showrooms', '_blank');
      }
    }),
    ...(offer === SUPPORTED_OFFERS.pl && {
      async onSeeInVoxbox() {
        window.open('/showrooms', '_blank');
      }
    }),
    ...(offer === SUPPORTED_OFFERS.pl && getPublicEnv('NEXT_PUBLIC_DISPLAY_DESIGN_STOR') === 'true' && {
      async onQuestionDesignStor() {
        window.location.hash = '#designStor';
      }
    }),
    ...(offer === SUPPORTED_OFFERS.mx && typeof window !== 'undefined' && {
      async onAskQuestion() {
        window.open('https://api.whatsapp.com/send/?phone=5215611547782&text&app_absent=0', '_blank');
      }
    }),
    ...((offer === SUPPORTED_OFFERS.pl || offer === SUPPORTED_OFFERS.global) && typeof window !== 'undefined' && typeof window.LiveChatWidget !== 'undefined' && {
      async onAskQuestion() {
        window?.LiveChatWidget?.call('maximize');
      }
    }),
    ecoTaxInfo: offer === SUPPORTED_OFFERS.eur,
    useNewEndpoint: true,
    collectionName: undefined,
    availabilityStatus: undefined,
    showAvailabilityStatus: true,
    seeInShowroomActive: offer === SUPPORTED_OFFERS.pl,
    showDeliveryInfo: true,
    disableMaterialNumber: true,
    legalGuaranteeNoticeUrls: getPublicEnv('NEXT_PUBLIC_URL_LEGAL_GUARANTEE_NOTICE'),
    showroomUrl: getShowroomUrl(),
    getProductUrl: getCrosslinkProductUrl,
    onProductNavigate: handleProductNavigate,
    withRating: getPublicEnv('NEXT_PUBLIC_DISPLAY_TRUSTMATE_REVIEWS') === 'true',
    ...(getPublicEnv('NEXT_PUBLIC_DISPLAY_TRUSTMATE_REVIEWS') === 'true' && {
      async onRatingClick() {
        scrollToElementById('trustmate-reviews');
      }
    }),
    getFirstShowroom,
    onProductCardDocumentClick: async (index: number) => {
      setIsProductCardDocumentsOpened(index);
    },
    creative: {
      lang,
      debugPanel: false,
      offer: offer,
      disableDeliveryInfo: false,
      seeInShowroomActive: false,
      baseUrl: CONFIGURATOR_CREATIVE_BASE_URL,
      presetUrl: 'https://vox-files.com/assets',
      preset: {
        id: configuratorData.id.toString(),
        code: undefined,
        name: undefined,
        codeErp: undefined
      },
      presetIndex: query.presetIndex ? parseInt(query.presetIndex as string || '0', 10) : 0,
      orderCondCalcUrl: `${CONFIGURATOR_URI}/api/dry-object/meta?metadata=creative.price_and_delivery&offer=${regionOffer}&storages[]=${regionStorage}&pricelists[]=${regionPriceList}&lang=${lang}`,
      objectStorageUrl: `${CONFIGURATOR_URI}/api/object?metadata=creative.images,creative.name,creative.iguana_upload&offer=${regionOffer}&storages[]=${regionStorage}&pricelists[]=${regionPriceList}`,
      productCustomerUrl: objectUuid ? `${CONFIGURATOR_URI}/api/literal-object/${encodeURIComponent(objectUuid)}` : '',
      ...(IS_SHOP === 'true' && {
        onSaveProject: region === AVAILABLE_REGIONS.euro ? undefined : async (_data: ICreativeData, _images: ICreativeImages, extraData: ICreativeExtraData) => {
          setCreativeModalType('saveProject');
          setValueUuId(extraData.objectUuid.uuid);
          setImageUrl(extraData.images.main.CART);
          handleScrollUp();
          setValueModal(true);
        },
        async onShareProject(_data: ICreativeData, _images: ICreativeImages, extraData: ICreativeExtraData) {
          setCreativeModalType('shareProject');
          setValueUuId(extraData.objectUuid.uuid);
          setImageUrl(extraData.images.main.CART);
          handleScrollUp();
          setValueModal(true);
        },
        async onAddToCart(data: ICreativeData, images: ICreativeImages, count: number, extraData: ICreativeExtraData) {
          const product: IConfiguratorCreativeProduct = {
            id: configuratorData.id,
            data,
            images,
            count,
            extraData
          };
          const cartProducts: ICartData[] = productCreativeAdapter(product);
          await addProducts(cartProducts, false);
        },
        async onAddToVoxBox(_data: ICreativeData, _images: ICreativeImages, extraData: ICreativeExtraData) {
          const {
            uuid
          } = extraData.objectUuid;
          if (extraData.iguanaUploadData && extraData.iguanaUploadData.jsonUploaded) {
            const locale = `&locale=${lang}`;
            const windowUrl = `${getPublicEnv('NEXT_PUBLIC_VOXBOX_URL')}/StreamingAssets/invenassetslotory-item-redirect.html?identifier=${uuid}&version=${uuid}${locale}`;
            window.open(windowUrl, '_blank');
          } else {
            logger.error(`Error while adding to voxbox ${uuid}`);
            alertsStore.pushAlert({
              message: t('saveToVoxboxError'),
              display: true
            });
          }
        },
        async onAskQuestion() {
          if (offer === SUPPORTED_OFFERS.mx && typeof window !== 'undefined') {
            window.open('https://api.whatsapp.com/send/?phone=5215611547782&text&app_absent=0', '_blank');
          } else if (typeof window.LiveChatWidget !== 'undefined') {
            window.LiveChatWidget.call('maximize');
          }
        },
        async onDownloadImages(data: ICreativeData, image: ICreativeImages) {
          const base = `vox_creative_${convertToSlug(data.productPreset.name)}_${data.productPreset.id}`;
          try {
            await downloadFile(image.main, `${base}_front.png`, 'image/png', {
              imageFace: 'main'
            });
            await downloadFile(image.inner, `${base}_inner.png`, 'image/png', {
              imageFace: 'inner'
            });
          } catch (e) {
            logger.error('onDownloadImages failed', e);
          }
        },
        ecoTaxInfo: DISPLAY_ECO_TAX_INFO === 'true'
      })
    },
    // configuratorApiBaseUrl: CONFIGURATOR_URI,
    productConfigApiResponse: config
  }) as IInitOptions, [configuratorConfig, objectUuid, cart, offer, configuratorData.id, configuratorData?.configuration?.uuid, configuratorData?.availabilities?.storages, addProducts, config, getShowroomUrl, getFirstShowroom, lang, region, handleScrollUp, handleProductNavigate, query.presetIndex]);
  const renderConfigurator = useMemo(() => <div sx={styles.configuratorContainer}>
          {!isConfiguratorReady && <Loader fullCover />}
          {isConfiguratorReady && isPageTransitioning && <div sx={styles.transitionOverlay} aria-busy="true" aria-live="polite">
              <Loader isFixed />
            </div>}
          <ConfiguratorFrame key={`product-frame-${productFrameKey}`} frameKey={productFrameKey} config={FrontConfiguratorConfig as IInitOptions} data={configuratorData} onReady={handleConfiguratorReady} />
        </div>, [isConfiguratorReady, isPageTransitioning, FrontConfiguratorConfig, configuratorData, productFrameKey, handleConfiguratorReady]);
  const openedProductCardDocumentBlocks = isProductCardDocumentsOpened !== null ? productCardLinksDocuments?.[isProductCardDocumentsOpened] : undefined;
  const renderProductCardDocumentTitle = useMemo(() => {
    if (isProductCardDocumentsOpened === null || productCardTitles.length === 0) return null;
    return productCardTitles[isProductCardDocumentsOpened];
  }, [isProductCardDocumentsOpened, productCardTitles]);
  useEffect(() => {
    handleSaveCreative();
    if (isConfiguratorReady) {
      handleOverflowHtml();
    }
  }, [isConfiguratorReady, handleSaveCreative, handleOverflowHtml]);
  useEffect(() => {
    if (!valueModal) {
      setProjectCreated(false);
    }
  }, [valueModal]);
  return <div ref={ref}>
        <CreativeModalContent modalType={creativeModalType} isUserLogged={sessionStatus === 'authenticated' ? session : null} active={valueModal} isProjectCreated={projectCreated} setProjectCreated={setProjectCreated} uuId={valueUuId} imageUrl={imageUrl} setValueModal={setValueModal} />
        {renderConfigurator}
        {offer === SUPPORTED_OFFERS.pl && <Aside data-cy="CartSideBar" active={isNearestStoresOpened} onCloseAside={() => setIsNearestStoresOpened(false)} hiddenButtonClose size="frontConfigurator">
            <NearestStores productId={configuratorConfig.productId} productName={configuratorData.name} column setAsideActive={setIsNearestStoresOpened} setFirstShowroom={setFirstShowroom} isDip={isDip()} limit={isNearestStoresOpened ? 500 : 1} useUserGeolocationInQuery={true} useMerchandisingDisplayStatuses={!isNearestStoresOpened} />
          </Aside>}
        {productCardLinksDocuments && <Aside data-cy="CartSideBar" active={isProductCardDocumentsOpened !== null} onCloseAside={() => setIsProductCardDocumentsOpened(null)} size="default" title={renderProductCardDocumentTitle ?? undefined} overflowY="visable" overflowX="hidden">
            {openedProductCardDocumentBlocks && <ProductCardDocument blocks={openedProductCardDocumentBlocks} />}
          </Aside>}
        <QuoteRequestModal isOpen={isQuoteRequestModalOpen} onClose={() => setIsQuoteRequestModalOpen(false)} quoteRequestData={quoteRequestData} configuratorData={configuratorData} />
      </div>;
});
FrontConfigurator.displayName = 'FrontConfigurator';
export default memo(FrontConfigurator);